import { IAgreement } from '@partiu/api/dashboard/types';
import { generateFixedColorGradient } from './colors';

export const MONTHS: string[] = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

export function mixArrays<T>(...arrays: T[][]): T[][] {
  const mixedArr: T[][] = [];
  const maxSize = Math.max(...arrays.map((a) => a.length));

  for (let i = 0; i <= maxSize; i++) {
    arrays.forEach((arr: T[]) => {
      const el: T[] = arr.pop() as T[];

      if (el) mixedArr.push(el);
    });
  }
  return mixedArr;
}

export function setBrowserThemeColor(color: string) {
  try {
    // @ts-ignore
    document.querySelector('meta[name="theme-color"]').content = color;
  } catch {}
}

enum ReadFunction {
  ReadAsDataURL = 'readAsDataURL',
  ReadAsText = 'readAsText',
}

export function loadFile(
  accept: string,
  onFileLoaded?: ((file: File) => void) | null,
  readerCallback?: ((file: string | ArrayBuffer | null) => void) | null,
  readFunction: ReadFunction = ReadFunction.ReadAsDataURL,
) {
  const element = document.createElement('input');
  const reader = new FileReader();

  element.type = 'file';
  element.accept = accept;
  document.body.append(element);

  reader.onload = (event) => {
    if (!event.target) return;

    if (readerCallback) readerCallback(reader.result);

    element.remove();
  };

  element.onchange = function (event) {
    const target = event.target as HTMLInputElement;

    if (!target || !target.files) return;

    const file = target.files[0];

    if (onFileLoaded) onFileLoaded(file)

    reader[readFunction](file);
  };

  element.click();
}

export function loadCSV(onCSVLoaded: (code: string) => void) {
  loadFile(
    '.csv',
    null,
    async (str) => {
      // @ts-ignore - Is always text because the next parameters (readAsText)
      onCSVLoaded(str);
    },
    ReadFunction.ReadAsText
  );
}

export interface CSVColumn {
  field: string;
  headerName: string;
  valueFormatter?: (value: any) => any;
}

export function downloadFile(fileName: string, href: string) {
  const element = document.createElement('a');
  element.setAttribute('href', href);
  element.setAttribute('download', fileName);

  element.style.display = 'none';
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
}

export function downloadCSV(fileName: string, headers: CSVColumn[], data: Record<string, any>[] | any[]) {
  const isDataArray = typeof data[0] === 'object';
  const headersText = headers.map((h) => h.headerName).join(',') + '\n';
  const dataText = !isDataArray
    ? data.join('\n')
    : data
        .map((d) =>
          headers.map((c) => {
            const valueFormatter =
              c.valueFormatter ||
              function (data) {
                return data;
              };
            return valueFormatter(d[c.field]);
          })
        )
        .join('\n');
  const result = headersText + dataText;

  downloadFile(`${fileName}.csv`, 'data:text/plain;charset=utf-8,' + encodeURIComponent(result));
}

export function updateThemeWithAgreement(
  agreement: IAgreement,
  originalColors: Record<string, any>,
  updateTheme: (data: Record<string, any>) => void
) {
  updateTheme({
    colors: {
      ...originalColors,
      primaryColor: agreement.primaryColor,
      primaryFontColor: agreement.primaryFontColor,
      menuColor: agreement.menuColor,
      menuFontColor: agreement.menuFontColor,
      secondaryColor: agreement.secondaryColor,
      secondaryFontColor: agreement.secondaryFontColor,
      graphColors: mixArrays(
        generateFixedColorGradient(agreement.primaryColor, 30),
        generateFixedColorGradient(agreement.secondaryColor, 20)
      ),
    },
    extraData: { ...agreement },
    id: agreement.id,
    name: agreement.name,
    logo: agreement.logo,
    email: agreement.email,
    telephone: agreement.telephone,
    isLoading: false,
    banners: agreement.banners,
  });
}

export interface ExcelSheetConfig<T = any> {
  name: string;
  rows: T[];
  headers?: CSVColumn[];
}

export async function downloadExcel(fileName: string, sheets: ExcelSheetConfig[]) {
  const { utils, writeFile } = await import('xlsx');

  const workbook = utils.book_new();

  sheets.forEach((sheet) => {
    let worksheet;

    if (sheet.headers && sheet.rows && sheet.rows.length > 0) {
      const headerRow = sheet.headers.map((h) => h.headerName);
      const dataRows = sheet.rows.map((row: Record<string, any>) =>
        sheet.headers!.map((h) => (h.valueFormatter ? h.valueFormatter((row as any)[h.field]) : (row as any)[h.field]))
      );
      const aoa = [headerRow, ...dataRows];
      worksheet = utils.aoa_to_sheet(aoa);
    } else {
      worksheet = utils.json_to_sheet(sheet.rows || []);
    }

    utils.book_append_sheet(workbook, worksheet, sheet.name.substring(0, 31));
  });

  writeFile(workbook, `${fileName}.xlsx`);
}
